<template>
  <div class="broadcast-list">
    <div
      class="selection-info"
      :class="{ highlight: !canSaveCustomMarket }"
    >
      <div class="selection-info-image">
        <img :src="selectionInfoIcon">
      </div>
      <span class="message">
        In order to create a custom market at least 8 stations must be selected
      </span>
    </div>
    <div class="broadcast-list-title">
      <div class="broadcast-count">
        <span v-if="!spinnerVisible">
          {{ broadcastsCount }} Stations found
          <span class="selected-count">({{ selectedBroadcastsCount }} selected)</span>
        </span>
      </div>
      <broadcast-list-filters class="broadcast-filters" />
    </div>
    <div class="header">
      <div class="toggle-select">
        <toggle-button
          :selected="isEveryFilteredBroadcastSelected"
          @toggle="toggleAllSelected"
        />
      </div>
      <div />
      <sortable-header-cell
        field="name"
        label="Broadcast Name"
      />
      <sortable-header-cell
        field="type"
        label="Band"
      />
      <sortable-header-cell
        field="hasLiveData"
        label="Live Data"
      />
    </div>
    <div
      v-if="spinnerVisible"
      class="broadcasts-loading"
    >
      <animated-icon
        class="loading-spinner"
        path="/static/animations/search-spinner.json"
      />
      <div>Loading search results</div>
    </div>
    <div
      v-else-if="!broadcastsCount"
      class="empty-results"
    >
      <div class="empty-results-header">
        We're Sorry!
      </div>
      <div class="empty-results-message">
        Your search didn’t return any results.
      </div>
    </div>
    <div
      v-else
      class="broadcasts"
    >
      <broadcast-item
        v-for="broadcast in filteredBroadcasts"
        :key="broadcast.id"
        class="broadcast"
        :broadcast="broadcast"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters, mapMutations } from 'vuex';
import AnimatedIcon from '@/components/common/AnimatedIcon';
import ToggleButton from '@/components/common/ToggleButton';
import SortableHeaderCell from './_SortableHeaderCell';
import BroadcastItem from './_BroadcastItem';
import BroadcastListFilters from './_BroadcastListFilters';

export default {
  name: 'BroadcastList',
  components: {
    AnimatedIcon,
    ToggleButton,
    SortableHeaderCell,
    BroadcastItem,
    BroadcastListFilters,
  },
  data() {
    return {
      spinnerVisible: false,
      spinnerVisibleTimeout: null,
    };
  },
  computed: {
    ...mapState('customMarket', ['loading']),
    ...mapGetters('customMarket', [
      'canSaveCustomMarket',
      'isEveryFilteredBroadcastSelected',
      'filteredBroadcasts',
      'selectedBroadcasts',
    ]),
    broadcastsCount() {
      return this.filteredBroadcasts.length;
    },
    selectedBroadcastsCount() {
      return this.selectedBroadcasts.length;
    },
    /* eslint-disable global-require, import/no-dynamic-require */
    selectionInfoIcon() {
      if (!this.canSaveCustomMarket) return require('@/assets/icon-red-dot.svg');
      return require('@/assets/icon-checkbox-green.svg');
    },
    /* eslint-enable global-require, import/no-dynamic-require */
  },
  watch: {
    loading: {
      immediate: true,
      handler() {
        if (!this.loading) {
          clearTimeout(this.spinnerVisibleTimeout);
          this.spinnerVisible = false;
          return;
        }
        if (_.isEmpty(this.filteredBroadcasts)) {
          this.spinnerVisible = true;
          return;
        }
        this.spinnerVisibleTimeout = setTimeout(() => {
          this.spinnerVisible = true;
        }, 250);
      },
    },
  },
  methods: {
    ...mapMutations('customMarket', ['selectBroadcasts', 'unselectBroadcasts']),
    toggleAllSelected() {
      const broadcastIds = _.map(this.filteredBroadcasts, 'id');
      if (this.isEveryFilteredBroadcastSelected) {
        this.unselectBroadcasts(broadcastIds);
      } else {
        this.selectBroadcasts(broadcastIds);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables";
@import "~@/assets/scss/mixins";

.selection-info {
  display: flex;
  height: 36px;
  box-sizing: border-box;
  overflow: hidden;
  align-items: center;
  padding: 0 16px;
  border-radius: 2px;
  background-color: rgba(0,0,0,.1);
  color: white;
  font-size: $size-5;
  font-weight: $weight-bold;
  line-height: 18px;
  margin-top: 16px;

  @include until($tablet) {
    height: 45px;
    font-size: $size-6;
  }

  &.highlight {
    background-color: #333333;
  }

  .selection-info-image {
    display: flex;
    align-items: center;
    width: 14px;
    margin-right: 12px;
  }
}

.broadcast-list-title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 16px 0 10px;
  @include until($tablet) {
    flex-direction: column;
  }
}

.broadcast-count {
  min-height: 30px;
  margin: 0 24px 0 0;
  font-weight: $weight-black;
  font-size: $size-3;
  line-height: 30px;
  color: white;

  @include until($tablet) {
    font-size: $size-5;
    margin-bottom: 13px;
    margin-right: 0;
  }

  .selected-count {
    color: #999999;
    font-size: 15px;
    line-height: 18px;
    font-weight: $weight-normal;
    margin-left: 5px;
  }
}

.broadcast-filters {
  margin-top: 3px;
}

.header, .broadcast {
  display: grid;
  grid-template-columns: 64px 66px 4fr 1fr 1fr;
  height: 54px;
  align-items: center;
  color: white;
  @include until($tablet) {
    grid-template-columns: 44px 52px 4fr 55px;
    font-size: $size-6;
    & > :last-child {
      display: none;
    }
  }
}

.header {
  font-size: $size-6;
  font-weight: $weight-bold;
  line-height: 16px;
}

.toggle-select {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.broadcasts-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 17px;
  line-height: 21px;

  .loading-spinner {
    margin-bottom: 17px;
    height: 69px;
    width: 69px;
  }
}

.empty-results {
  margin-top: 100px;
  color: white;
  text-align: center;

  .empty-results-header {
    margin-bottom: 15px;
    font-size: 25px;
    font-weight: $weight-bold;
    line-height: 30px;
  }

  .empty-results-message {
    opacity: 0.5;
    font-size: 17px;
    line-height: 21px;
  }
}
</style>
